import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store/store';
import { navigate } from '../redux/slice/layoutSlice';
import { getModule } from '../config/module-config';
import { getIcon } from '../components/icons';
import type { XMenuItem, XRole } from '../config/module-types';
import { useGetServerVersionQuery } from '../redux/api';
import { useT } from '../i18n';
import { SubMenu } from './SubMenu';
import { SessionCountdown } from './SessionCountdown';

const MOCK_TASK_COUNT = 5;

// Exact role match (one role per user). The Platform Owner is the only role
// that sees everything; otherwise an item is shown only to its exact role.
const canSee = (role: XRole, allowedRole?: XRole): boolean =>
  !allowedRole || role === 'platform owner' || role === allowedRole;

const stripQuery = (path: string) => path.split('?')[0];

// Left sidebar — renders the active module's menu, adapted from webapp2's Menu.
export const Menu = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeModuleId, currentPath, sidebarOpen, user } = useSelector(
    (s: RootState) => s.layout
  );
  const collapsed = !sidebarOpen;
  const activeModule = getModule(activeModuleId);
  const t = useT();

  // Menu labels are translated by item id — `nav.products` for the Products
  // item, and so on (see i18n/translations.ts). The English label from
  // module-config is the fallback, so an item with no entry yet still reads as
  // a name rather than as a raw key.
  const label = (item: XMenuItem) => t(`nav.${item.id}`, item.label);

  const { data: serverVersion } = useGetServerVersionQuery();

  const items = (activeModule?.menu ?? []).filter((item) => canSee(user.role, item.allowedRole));

  // Auto-open any group that currently contains the active route.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    items.forEach((item) => {
      if (item.children?.some((c) => stripQuery(c.path) === currentPath)) {
        initial[item.id] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderIcon = (name?: string, size = 18) => {
    const Icon = getIcon(name);
    return Icon ? <Icon size={size} /> : <span className="nav-item__icon-blank" />;
  };

  const renderLeaf = (item: XMenuItem, nested = false) => {
    const isActive = stripQuery(item.path) === currentPath;
    return (
      <button
        key={item.id}
        type="button"
        className={`nav-item${nested ? ' nav-item--nested' : ''}${isActive ? ' is-active' : ''}`}
        onClick={() => dispatch(navigate(item.path))}
        title={collapsed ? label(item) : undefined}
      >
        <span className="nav-item__icon">{renderIcon(item.icon)}</span>
        {!collapsed && <span className="nav-item__label">{label(item)}</span>}
        {!collapsed && item.showCount && (
          <span className="nav-item__count">{MOCK_TASK_COUNT}</span>
        )}
      </button>
    );
  };

  return (
    <nav className={`sidebar${collapsed ? ' is-collapsed' : ''}`} aria-label="Main navigation">
      <div className="sidebar__items">
        {items.map((item) => {
          if (item.children?.length) {
            const children = item.children.filter((c) => canSee(user.role, c.allowedRole));
            return (
              <SubMenu
                key={item.id}
                name={label(item)}
                icon={renderIcon(item.icon)}
                isOpen={!!openGroups[item.id]}
                collapsed={collapsed}
                onToggle={() => toggleGroup(item.id)}
              >
                {children.map((child) => renderLeaf(child, true))}
              </SubMenu>
            );
          }
          return renderLeaf(item);
        })}
      </div>
      {!collapsed && (
        <div className="sidebar__footer">
          <span>{activeModule ? t(`module.${activeModule.id}`, activeModule.name) : null}</span>
          <div className="sidebar__meta">
            <span className="sidebar__versions">
              <span>W{__APP_VERSION__}</span>
              <span>S{serverVersion ?? '—'}</span>
            </span>
            <SessionCountdown />
          </div>
        </div>
      )}
    </nav>
  );
};
